import { ModalContext } from 'app/app';
import axios from 'axios';
import { FieldInput } from 'components/fieldInput';
import { Switch, ViewContext } from 'components/lib';
import { Form, Formik } from 'formik';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import Loader from './images/loader.gif';
import styles from './styles.module.scss';

export function DriversModal(props) {
  const { id: company_id } = useParams();
  const context = useContext(ViewContext);
  const { modal, setModal } = useContext(ModalContext);
  const [loading, setLoading] = useState(false);
  const [excludeFromPolicy, setExcludeFromPolicy] = useState(false);
  const [driverLicense, setDriverLicense] = useState('');

  const api_error = false;

  const minDriverAge =
    modal?.data?.minDriverAge ?? (modal.mode === 'run_quote' ? 23 : 18);

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required('This field is required!'),
    last_name: Yup.string().required('This field is required!'),
    license_number: Yup.string().required('This field is required!'),
    license_state: Yup.string().required('This field is required!'),
    years_of_experience: Yup.number().required(
      'This field is required!'
    ),
    date_of_birth: Yup.string()
      .required('This field is required!')
      .test(
        'min-driver-age',
        `Driver must be at least ${minDriverAge} years old`,
        (value) => {
          if (!value) return false;
          const dob = new Date(value);
          if (Number.isNaN(dob.getTime())) return false;
          const today = new Date();
          let age = today.getFullYear() - dob.getFullYear();
          const monthDiff = today.getMonth() - dob.getMonth();
          const dayDiff = today.getDate() - dob.getDate();
          if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
            age -= 1;
          }
          return age >= minDriverAge;
        }
      ),
    date_of_hire: Yup.string(),
    status: Yup.string(),
    accidents: Yup.string().required('This field is required!'),
    violations: Yup.string().required('This field is required!'),
    suspensions: Yup.string().required('This field is required!'),
  });

  const maxDob = new Date();
  maxDob.setFullYear(maxDob.getFullYear() - minDriverAge);
  const maxDobValue = maxDob.toISOString().substring(0, 10);

  useEffect(() => {
    if (modal.data && modal.data.exclude_from_policy) {
      setExcludeFromPolicy(modal.data.exclude_from_policy);
    } else {
      setExcludeFromPolicy(false);
    }
  }, [modal]);

  return (
    <Fragment>
      {modal.name === 'driver_modal' && (
        <div className={styles.background}>
          <div className={styles.wrapper}>
            <div className={styles.insideWrapper}>
              <div className={styles.title}>
                {modal.mode === 'edit' ? 'Edit Driver' : 'Add Driver'}
              </div>
              <div className={styles.line} />
              <Formik
                validationSchema={validationSchema}
                initialValues={{
                  first_name:
                    modal.data && modal.data.name
                      ? modal.data.name.split(' ')[0]
                      : '',
                  last_name:
                    modal.data && modal.data.name
                      ? modal.data.name.split(' ')[1]
                      : '',
                  license_number:
                    modal.data && modal.data['DL Number']
                      ? modal.data['DL Number']
                      : '',
                  license_state:
                    modal.data && modal.data['State']
                      ? modal.data['State']
                      : 'Alabama',
                  accidents:
                    modal.data && modal.data.accidents
                      ? modal.data.accidents
                      : '',
                  violations:
                    modal.data && modal.data.violations
                      ? modal.data.violations
                      : '',
                  suspensions:
                    modal.data && modal.data.suspensions
                      ? modal.data.suspensions
                      : '',
                  excludeFromPolicy,
                  date_of_birth:
                    modal.data && modal.data['Date of Birth']
                      ? new Date(modal.data['Date of Birth'])
                          .toISOString()
                          .substring(0, 10)
                      : '',
                  date_of_hire:
                    modal.data && modal.data['Date of Hire']
                      ? new Date(modal.data['Date of Hire'])
                          .toISOString()
                          .substring(0, 10)
                      : '',
                  years_of_experience: modal.data
                    ? modal.data['Years of Experience']
                    : '',
                  status:
                    modal.data && modal.data.status
                      ? modal.data.status
                      : 'active',
                }}
                onSubmit={async (values) => {
                  setLoading(true);

                  if (modal.mode !== 'run_quote') {
                    if (modal.data) {
                      await axios
                        .patch(`/api/driver/${modal.data.id}`, {
                          name:
                            values.first_name +
                            ' ' +
                            values.last_name,
                          license_number: values.license_number,
                          license_state: values.license_state,
                          date_of_birth: values.date_of_birth,
                          date_of_hire: values.date_of_hire,
                          accidents: values.accidents,
                          violations: values.violations,
                          suspensions: values.suspensions,
                          exclude_from_policy:
                            values.excludeFromPolicy,
                          years_of_experience: JSON.stringify(
                            values.years_of_experience
                          ).replaceAll('"', ''),
                          company_id,
                          status: values.status,
                        })
                        .then(async (res) => {
                          const front_license_file =
                            document.getElementById('front_license')
                              .files[0];

                          const back_license_file =
                            document.getElementById('back_license')
                              .files[0];

                          if (
                            front_license_file ||
                            back_license_file
                          ) {
                            const form_data = new FormData();

                            if (front_license_file) {
                              form_data.append(
                                'front_license',
                                front_license_file
                              );
                            }

                            if (back_license_file) {
                              form_data.append(
                                'back_license',
                                back_license_file
                              );
                            }

                            await axios
                              .post(
                                `/api/driver/${modal.data.id}/file`,
                                form_data,
                                {
                                  headers: {
                                    'Content-Type':
                                      'multipart/form-data',
                                  },
                                }
                              )
                              .catch((fileErr) => {
                                context.notification.show(
                                  'Failed on upload file to server.',
                                  'error',
                                  true,
                                  'toast',
                                  'x-circle'
                                );
                              });
                          }

                          setLoading(false);
                          setModal({});

                          context.notification.show(
                            'Driver updated successfully!',
                            'success',
                            true,
                            'toast',
                            'check-circle'
                          );

                          modal.action();
                        })
                        .catch((err) => {
                          setLoading(false);
                          setModal({});

                          context.notification.show(
                            err.response.data.message,
                            'error',
                            true,
                            'toast',
                            'x-circle'
                          );
                        });
                    } else {
                      await axios
                        .post(`/api/driver`, {
                          name:
                            values.first_name +
                            ' ' +
                            values.last_name,
                          license_number: values.license_number,
                          license_state: values.license_state,
                          date_of_birth: values.date_of_birth,
                          date_of_hire: values.date_of_hire,
                          accidents: values.accidents,
                          violations: values.violations,
                          suspensions: values.suspensions,
                          exclude_from_policy:
                            values.excludeFromPolicy,
                          years_of_experience: JSON.stringify(
                            values.years_of_experience
                          ),
                          company_id,
                        })
                        .then(async (res) => {
                          const front_license_file =
                            document.getElementById('front_license')
                              .files[0];

                          const back_license_file =
                            document.getElementById('back_license')
                              .files[0];

                          if (
                            front_license_file ||
                            back_license_file
                          ) {
                            const form_data = new FormData();

                            if (front_license_file) {
                              form_data.append(
                                'front_license',
                                front_license_file
                              );
                            }

                            if (back_license_file) {
                              form_data.append(
                                'back_license',
                                back_license_file
                              );
                            }

                            await axios
                              .post(
                                `/api/driver/${res.data.data.id}/file`,
                                form_data,
                                {
                                  headers: {
                                    'Content-Type':
                                      'multipart/form-data',
                                  },
                                }
                              )
                              .then((res) => {
                                setLoading(false);
                                setModal({});

                                context.notification.show(
                                  'Driver created successfully!',
                                  'success',
                                  true,
                                  'toast',
                                  'check-circle'
                                );

                                modal.action();
                              })
                              .catch((fileErr) => {
                                context.notification.show(
                                  'Failed on upload file to server.',
                                  'error',
                                  true,
                                  'toast',
                                  'x-circle'
                                );
                              });
                          } else {
                            setLoading(false);
                            setModal({});

                            context.notification.show(
                              'Driver created successfully!',
                              'success',
                              true,
                              'toast',
                              'check-circle'
                            );

                            modal.action();
                          }
                        })
                        .catch((err) => {
                          setLoading(false);
                          setModal({});

                          context.notification.show(
                            err.response.data.message,
                            'error',
                            true,
                            'toast',
                            'x-circle'
                          );
                        });
                    }
                  } else {
                    await axios
                      .post(`/api/driver`, {
                        name:
                          values.first_name + ' ' + values.last_name,
                        license_number: values.license_number,
                        license_state: values.license_state,
                        date_of_birth: values.date_of_birth,
                        date_of_hire: values.date_of_hire,
                        accidents: values.accidents,
                        violations: values.violations,
                        suspensions: values.suspensions,
                        exclude_from_policy: values.excludeFromPolicy,
                        years_of_experience:
                          values.years_of_experience,
                        company_id: modal.data.company_id,
                      })
                      .then(async (res) => {
                        modal.action({
                          id: res.data.data.id,
                          name:
                            values.first_name +
                            ' ' +
                            values.last_name,
                          license_number: values.license_number,
                          license_state: values.license_state,
                          date_of_birth: values.date_of_birth,
                          date_of_hire: values.date_of_hire,
                          accidents: values.accidents,
                          violations: values.violations,
                          suspensions: values.suspensions,
                          exclude_from_policy:
                            values.excludeFromPolicy,
                          years_of_experience:
                            values.years_of_experience,
                          company_id: modal.data.company_id,
                        });

                        const front_license_file =
                          document.getElementById('front_license')
                            .files[0];

                        const back_license_file =
                          document.getElementById('back_license')
                            .files[0];

                        if (front_license_file || back_license_file) {
                          const form_data = new FormData();

                          if (front_license_file) {
                            form_data.append(
                              'front_license',
                              front_license_file
                            );
                          }

                          if (back_license_file) {
                            form_data.append(
                              'back_license',
                              back_license_file
                            );
                          }

                          await axios
                            .post(
                              `/api/driver/${res.data.data.id}/file`,
                              form_data,
                              {
                                headers: {
                                  'Content-Type':
                                    'multipart/form-data',
                                },
                              }
                            )
                            .then((res2) => {
                              setLoading(false);
                              setModal({});

                              context.notification.show(
                                'Driver created successfully!',
                                'success',
                                true,
                                'toast',
                                'check-circle'
                              );
                            })
                            .catch((fileErr) => {
                              console.log(fileErr);
                              context.notification.show(
                                'Failed on upload file to serve r.',
                                'error',
                                true,
                                'toast',
                                'x-circle'
                              );
                            });
                        } else {
                          setLoading(false);
                          setModal({});

                          context.notification.show(
                            'Driver created successfully!',
                            'success',
                            true,
                            'toast',
                            'check-circle'
                          );
                        }
                      })
                      .catch((err) => {
                        setLoading(false);
                        setModal({});

                        context.notification.show(
                          err.response.data.message,
                          'error',
                          true,
                          'toast',
                          'x-circle'
                        );
                      });
                  }
                }}
              >
                {({
                  errors,
                  touched,
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  submitForm,
                  setFieldValue,
                }) => (
                  <Fragment>
                    <Form
                      className={styles.form}
                      onSubmit={handleSubmit}
                    >
                      <div className={styles.fieldRow}>
                        <FieldInput
                          id="first_name"
                          type="text"
                          label="First Name"
                          placeholder="First Name"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.first_name}
                          touched={touched.first_name}
                          required
                        />
                        <FieldInput
                          id="last_name"
                          type="text"
                          label="Last Name"
                          placeholder="Last Name"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.last_name}
                          touched={touched.last_name}
                          required
                        />
                        <FieldInput
                          id="date_of_birth"
                          type="date"
                          label="Date of Birth"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.date_of_birth}
                          touched={touched.date_of_birth}
                          maxDate={maxDobValue}
                        />
                        <FieldInput
                          id="date_of_hire"
                          type="date"
                          label="Date of Hire"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.date_of_hire}
                          touched={touched.date_of_hire}
                        />
                        {modal.mode === 'edit' ? (
                          <FieldInput
                            id="status"
                            type="select"
                            label="Status"
                            placeholder="Insert vehicle's status"
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            showErrors
                            errors={errors.status}
                            touched={touched.status}
                            required
                            options={[
                              {
                                value: 'active',
                                label: 'Active',
                              },
                              {
                                value: 'inactive',
                                label: 'Inactive',
                              },
                            ]}
                          />
                        ) : null}
                      </div>
                      <div className={styles.fieldRow}>
                        <FieldInput
                          id="years_of_experience"
                          type="number"
                          label="Years of Experience"
                          placeholder="Years of Experience"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.years_of_experience}
                          touched={touched.years_of_experience}
                          required
                        />
                        <FieldInput
                          id="license_state"
                          type="select"
                          label="License State"
                          placeholder="License State"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.license_state}
                          touched={touched.license_state}
                          required
                          options={[
                            { value: 'Alabama', label: 'AL' },
                            { value: 'Alaska', label: 'AK' },
                            { value: 'Arizona', label: 'AZ' },
                            { value: 'Arkansas', label: 'AR' },
                            { value: 'California', label: 'CA' },
                            { value: 'Colorado', label: 'CO' },
                            { value: 'Connecticut', label: 'CT' },
                            { value: 'Delaware', label: 'DE' },
                            { value: 'Florida', label: 'FL' },
                            { value: 'Georgia', label: 'GA' },
                            { value: 'Hawaii', label: 'HI' },
                            { value: 'Idaho', label: 'ID' },
                            { value: 'Illinois', label: 'IL' },
                            { value: 'Indiana', label: 'IN' },
                            { value: 'Iowa', label: 'IA' },
                            { value: 'Kansas', label: 'KS' },
                            { value: 'Kentucky', label: 'KY' },
                            { value: 'Louisiana', label: 'LA' },
                            { value: 'Maine', label: 'ME' },
                            { value: 'Maryland', label: 'MD' },
                            { value: 'Massachusetts', label: 'MA' },
                            { value: 'Michigan', label: 'MI' },
                            { value: 'Minnesota', label: 'MN' },
                            { value: 'Mississippi', label: 'MS' },
                            { value: 'Missouri', label: 'MO' },
                            { value: 'Montana', label: 'MT' },
                            { value: 'Nebraska', label: 'NE' },
                            { value: 'Nevada', label: 'NV' },
                            { value: 'New Hampshire', label: 'NH' },
                            { value: 'New Jersey', label: 'NJ' },
                            { value: 'New Mexico', label: 'NM' },
                            { value: 'New York', label: 'NY' },
                            { value: 'North Carolina', label: 'NC' },
                            { value: 'North Dakota', label: 'ND' },
                            { value: 'Ohio', label: 'OH' },
                            { value: 'Oklahoma', label: 'OK' },
                            { value: 'Oregon', label: 'OR' },
                            { value: 'Pennsylvania', label: 'PA' },
                            { value: 'Rhode Island', label: 'RI' },
                            { value: 'South Carolina', label: 'SC' },
                            { value: 'South Dakota', label: 'SD' },
                            { value: 'Tennessee', label: 'TN' },
                            { value: 'Texas', label: 'TX' },
                            { value: 'Utah', label: 'UT' },
                            { value: 'Vermont', label: 'VT' },
                            { value: 'Virginia', label: 'VA' },
                            { value: 'Washington', label: 'WA' },
                            { value: 'West Virginia', label: 'WV' },
                            { value: 'Wisconsin', label: 'WI' },
                            { value: 'Wyoming', label: 'WY' },
                          ]}
                        />
                        <FieldInput
                          id="license_number"
                          type="text"
                          label="License Number"
                          placeholder="License Number"
                          value={driverLicense}
                          handleChange={(e) => {
                            setDriverLicense(
                              e.target.value.replaceAll('-', '')
                            );
                            e.target.value =
                              e.target.value.replaceAll('-', '');
                            handleChange(e);
                          }}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.license_number}
                          touched={touched.license_number}
                          required
                        />
                      </div>
                      <div className={styles.fieldRow}>
                        <FieldInput
                          id="front_license"
                          type="upload"
                          label="Front License Picture"
                          handleChange={(file) => {
                            handleChange('front_license');
                            setFieldValue('front_license', file);
                          }}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.front_license}
                          touched={touched.front_license}
                        />
                        <FieldInput
                          id="back_license"
                          type="upload"
                          label="Back License Picture"
                          handleChange={(file) => {
                            handleChange('back_license');
                            setFieldValue('back_license', file);
                          }}
                          handleBlur={handleBlur}
                          showErrors
                          errors={errors.back_license}
                          touched={touched.back_license}
                        />
                      </div>
                      <div
                        className={styles.fieldRow}
                        style={{
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Switch
                          name="excludefrompolicy"
                          label="Exclude this driver from the policy?"
                          labelRight
                          labelClassName="font-bold"
                          onChange={(e) => {
                            setExcludeFromPolicy(
                              excludeFromPolicy === false
                                ? true
                                : false
                            );
                          }}
                          default={
                            modal.data &&
                            modal.data.exclude_from_policy
                              ? modal.data.exclude_from_policy
                              : excludeFromPolicy
                          }
                        />
                        <span>MVR Summary (prior 36 months)</span>
                      </div>
                      <div className={styles.fieldRow}>
                        <FieldInput
                          id="accidents"
                          type="text"
                          label="Accidents"
                          placeholder="Accidents"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.accidents}
                          touched={touched.accidents}
                        />
                        <FieldInput
                          id="violations"
                          type="text"
                          label="Violations"
                          placeholder="Violations"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.violations}
                          touched={touched.violations}
                        />
                        <FieldInput
                          id="suspensions"
                          type="text"
                          label="Suspensions"
                          placeholder="Suspensions"
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          required
                          showErrors
                          errors={errors.suspensions}
                          touched={touched.suspensions}
                        />
                      </div>
                    </Form>
                    <div
                      className={styles.footer}
                      style={
                        api_error
                          ? { justifyContent: 'space-between' }
                          : { justifyContent: 'flex-end' }
                      }
                    >
                      <div className={styles.buttons}>
                        <button
                          className={styles.cancel}
                          onClick={() => setModal('')}
                        >
                          Cancel
                        </button>
                        <button
                          className={styles.save}
                          onClick={!loading ? submitForm : null}
                          style={
                            loading
                              ? { cursor: 'default' }
                              : { cursor: 'pointer' }
                          }
                        >
                          {loading ? (
                            <img
                              src={Loader}
                              alt="Loader"
                              className={styles.loader}
                            />
                          ) : (
                            'Save'
                          )}
                        </button>
                      </div>
                    </div>
                  </Fragment>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
}
